import React, { useEffect, useRef, useState } from 'react';

import './blockEdit.css';
import { widthLabel } from './RowSplitter';
import type { Block, BlockFamily } from './types';
import type { BoardEditor } from './useBoardEdit';

type ResizeHandleProps = {
  block: Block;
  family?: BlockFamily;
  frame: React.RefObject<HTMLDivElement>;
  editor: BoardEditor;
};

type Drag = { startY: number; height: number; content: number; rowLeft: number; rowWidth: number };

export const SIZED: Array<BlockFamily | undefined> = ['time', 'breakdown', 'bridge', 'table', 'figures'];
export const PRESETS: Array<[string | undefined, number]> = [
  ['compact', 200],
  [undefined, 300],
  ['tall', 460],
];
const SNAP_PX = 6;
const MIN_CONTENT_PX = 120;
const MIN_SPAN = 2;

export const CONTENT = '.chart, .tbl, .rank, .figs';

export const presetsFor = (family?: BlockFamily) => (family === 'figures' ? [] : PRESETS);

export const minContentFor = (family?: BlockFamily) => (family === 'figures' ? 56 : MIN_CONTENT_PX);

export function heightPatch(family: BlockFamily | undefined, next: { size?: string; h?: number }): Record<string, unknown> {
  if (family === 'figures') return { h: next.h ?? null };
  return next.h !== undefined ? { h: next.h, size: null } : { size: next.size ?? null, h: null };
}

const ARROWS: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
const KEY_STEP_PX = 20;
const NOTE_MS = 1400;

/** Columns the block's cell spans now, read from the grid because unset widths share what is left. */
function spanNow(cell: HTMLElement): number | undefined {
  const row = cell.closest<HTMLElement>('.row');
  if (!row) return undefined;
  const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
  return ((cell.getBoundingClientRect().width + gap) / (row.getBoundingClientRect().width + gap)) * 12;
}

export default function ResizeHandle({ block, family, frame, editor }: ResizeHandleProps) {
  const drag = useRef<Drag | undefined>(undefined);
  const latest = useRef({ block, family, editor });
  latest.current = { block, family, editor };
  const [note, setNote] = useState<string>();
  useEffect(() => {
    if (!note) return undefined;
    const timer = window.setTimeout(() => setNote(undefined), NOTE_MS);
    return () => window.clearTimeout(timer);
  }, [note]);
  useEffect(() => {
    const resizeByKey = (event: KeyboardEvent) => {
      const step = event.altKey && !event.metaKey && !event.ctrlKey ? ARROWS[event.key] : undefined;
      const host = frame.current;
      if (!step || !host || !host.contains(event.target as Node) || (event.target as HTMLElement).closest('input, textarea, select, [contenteditable="true"]')) return;
      event.preventDefault();
      event.stopPropagation();
      const { block: at, family: kind, editor: edit } = latest.current;
      if (edit.pending) return;
      if (step[0]) {
        const cell = host.closest<HTMLElement>('.col');
        const span = cell ? spanNow(cell) : undefined;
        if (span === undefined) return;
        const next = Math.min(12, Math.max(MIN_SPAN, Math.round(span) + step[0]));
        const carrier = cell!.querySelector<HTMLElement>('[data-lens-block]')?.dataset.lensBlock ?? at.id;
        if (next !== Math.round(span)) edit.apply({ op: 'set', id: carrier, patch: { w: next } });
        setNote(widthLabel(next));
        return;
      }
      if (!SIZED.includes(kind)) return;
      const content = host.querySelector<HTMLElement>(CONTENT)?.getBoundingClientRect().height ?? 0;
      const height = Math.max(minContentFor(kind), Math.round(content + step[1] * KEY_STEP_PX));
      const snapped = presetsFor(kind).find(([, px]) => Math.abs(px - height) <= SNAP_PX);
      edit.apply({ op: 'set', id: at.id, patch: heightPatch(kind, snapped ? { size: snapped[0] } : { h: height }) });
      setNote(`Height ${snapped ? snapped[1] : height} px`);
    };
    window.addEventListener('keydown', resizeByKey, true);
    return () => window.removeEventListener('keydown', resizeByKey, true);
  }, [frame]);
  const [preview, setPreview] = useState<{ size?: string; h?: number; height: number; rowLeft: number; rowWidth: number }>();
  const cancel = () => {
    if (!drag.current) return;
    drag.current = undefined;
    setPreview(undefined);
  };
  const noted = note ? (
    <div aria-live="polite" className="resize-note" role="status">
      {note}
    </div>
  ) : null;
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !drag.current) return;
      event.stopPropagation();
      cancel();
    };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, []);
  if (!SIZED.includes(family)) return noted;
  const measure = (event: React.PointerEvent): { size?: string; h?: number; height: number; rowLeft: number; rowWidth: number } => {
    const deltaY = event.clientY - drag.current!.startY;
    const content = Math.max(minContentFor(family), Math.round(drag.current!.content + deltaY));
    const snapped = presetsFor(family).find(([, px]) => Math.abs(px - content) <= SNAP_PX);
    return {
      ...(snapped ? { size: snapped[0] } : { h: content }),
      height: Math.max(48, drag.current!.height + (snapped ? snapped[1] : content) - drag.current!.content),
      rowLeft: drag.current!.rowLeft,
      rowWidth: drag.current!.rowWidth,
    };
  };
  const handle = (className: string) => (
    <span
      aria-hidden
      className={className}
      onLostPointerCapture={cancel}
      onPointerCancel={cancel}
      onPointerDown={(event) => {
        if (!frame.current || editor.pending) return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        const box = frame.current.getBoundingClientRect();
        const row = (frame.current.closest('.row') ?? frame.current).getBoundingClientRect();
        const content = frame.current.querySelector<HTMLElement>(CONTENT)?.getBoundingClientRect().height ?? box.height;
        drag.current = { startY: event.clientY, height: box.height, content, rowLeft: row.left - box.left, rowWidth: row.width };
        setPreview({ size: block.size as string | undefined, ...drag.current });
      }}
      onPointerMove={(event) => drag.current && setPreview(measure(event))}
      onPointerUp={(event) => {
        if (!drag.current) return;
        const next = measure(event);
        const moved = Math.abs(event.clientY - drag.current.startY) > SNAP_PX;
        drag.current = undefined;
        setPreview(undefined);
        if (!moved) return;
        if (next.h !== undefined ? next.h !== block.h : next.size !== block.size || block.h) editor.apply({ op: 'set', id: block.id, patch: heightPatch(family, next) });
      }}
    />
  );

  return (
    <>
      {handle('resize-bottom')}
      {handle('resize')}
      {noted}
      {preview ? (
        <>
          <div className="resize-ghost" style={{ width: '100%', height: preview.height }}>
            <span>{preview.h ? `Height ${preview.h} px` : `Height ${preview.size ?? 'regular'}`}</span>
          </div>
          <div className="row-line" style={{ top: preview.height, left: preview.rowLeft, width: preview.rowWidth }} />
        </>
      ) : null}
    </>
  );
}
